import React, { useState, useEffect } from 'react';
import { commpeakService, CallHistory } from '../services/CommpeakService';
import './CallHistoryModal.css';

interface CallHistoryModalProps {
  leadId: string;
  leadName: string;
  leadPhone: string;
  isOpen: boolean;
  onClose: () => void;
}

const CallHistoryModal: React.FC<CallHistoryModalProps> = ({
  leadId,
  leadName,
  leadPhone,
  isOpen,
  onClose
}) => {
  const [callHistory, setCallHistory] = useState<CallHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeCall, setActiveCall] = useState<string | null>(null);
  const [callerNumber, setCallerNumber] = useState<string>('+1234567890');
  const [callNotes, setCallNotes] = useState<string>('');

  const loadCallHistory = React.useCallback(() => {
    const history = commpeakService.getLeadCallHistory(leadId);
    setCallHistory(history.reverse()); // Show newest first
  }, [leadId]);

  useEffect(() => {
    if (isOpen) {
      loadCallHistory();
    }
  }, [isOpen, leadId, loadCallHistory]);

  const initiateCall = async () => {
    if (!commpeakService.isValidPhoneNumber(leadPhone)) {
      alert('Invalid phone number format');
      return;
    }

    setLoading(true);
    setActiveCall('initiating');

    try {
      const response = await commpeakService.initiateCall(
        callerNumber,
        commpeakService.formatPhoneNumber(leadPhone),
        leadId
      );

      if (response.success) {
        setActiveCall(response.callId || 'active');
        alert('Call initiated successfully! Check your phone.');
        loadCallHistory();
        
        // Simulate call status updates (in real implementation, you'd use webhooks)
        setTimeout(() => {
          if (response.callId) {
            commpeakService.updateCallStatus(response.callId, 'connected');
            loadCallHistory();
          }
        }, 5000);
      } else {
        alert(`Failed to initiate call: ${response.message}`);
        setActiveCall(null);
      }
    } catch (error) {
      console.error('Call initiation error:', error);
      alert('Failed to initiate call. Please try again.');
      setActiveCall(null);
    } finally {
      setLoading(false);
    }
  };

  const endCall = async () => {
    if (!activeCall || activeCall === 'initiating') return;

    try {
      await commpeakService.endCall(activeCall);
      await commpeakService.updateCallStatus(activeCall, 'completed', undefined, callNotes);
      setActiveCall(null);
      setCallNotes('');
      loadCallHistory();
      alert('Call ended successfully');
    } catch (error) {
      console.error('Error ending call:', error);
      alert('Failed to end call');
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: CallHistory['status']) => {
    switch (status) {
      case 'connected': return '#4CAF50';
      case 'completed': return '#2196F3';
      case 'failed': return '#f44336';
      case 'no-answer': return '#FF9800';
      case 'initiated': return '#9C27B0';
      default: return '#666';
    }
  };

  const getStatusIcon = (status: CallHistory['status']) => {
    switch (status) {
      case 'connected': return '🟢';
      case 'completed': return '✅';
      case 'failed': return '❌';
      case 'no-answer': return '📵';
      case 'initiated': return '📞';
      default: return '⚪';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="call-modal-overlay" onClick={onClose}>
      <div className="call-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="call-modal-header">
          <h3>📞 Call Center - {leadName}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="call-modal-body">
          {/* Call Controls */}
          <div className="call-controls">
            <div className="call-info">
              <div className="lead-details">
                <strong>{leadName}</strong>
                <span>{leadPhone}</span>
              </div>
              
              <div className="caller-selection">
                <label>Call from:</label>
                <select 
                  value={callerNumber} 
                  onChange={(e) => setCallerNumber(e.target.value)}
                  disabled={!!activeCall}
                >
                  {commpeakService.getAvailableNumbers().map((number: string) => (
                    <option key={number} value={number}>{number}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="call-actions">
              {!activeCall ? (
                <button 
                  className="call-btn initiate"
                  onClick={initiateCall}
                  disabled={loading}
                >
                  {loading ? '📞 Calling...' : '📞 Call Now'}
                </button>
              ) : (
                <div className="active-call">
                  <div className="call-status">
                    {activeCall === 'initiating' ? '📞 Connecting...' : '🟢 Call Active'}
                  </div>
                  
                  <div className="call-notes">
                    <textarea
                      placeholder="Call notes..."
                      value={callNotes}
                      onChange={(e) => setCallNotes(e.target.value)}
                      rows={3}
                    />
                  </div>

                  <button 
                    className="call-btn end"
                    onClick={endCall}
                    disabled={activeCall === 'initiating'}
                  >
                    📴 End Call
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Call History */}
          <div className="call-history">
            <h4>📋 Call History</h4>
            
            {callHistory.length === 0 ? (
              <div className="no-history">
                <p>No previous calls</p>
                <small>Make your first call to this lead</small>
              </div>
            ) : (
              <div className="history-list">
                {callHistory.map((call) => (
                  <div key={call.id} className="history-item">
                    <div className="call-summary">
                      <div className="call-status-info">
                        <span className="status-icon">{getStatusIcon(call.status)}</span>
                        <span 
                          className="status-text"
                          style={{ color: getStatusColor(call.status) }}
                        >
                          {call.status.charAt(0).toUpperCase() + call.status.slice(1)}
                        </span>
                      </div>
                      
                      <div className="call-details">
                        <div className="call-time">
                          {new Date(call.startTime).toLocaleString()}
                        </div>
                        <div className="call-duration">
                          Duration: {formatDuration(call.duration)}
                        </div>
                        {call.callId && (
                          <div className="call-id">
                            ID: {call.callId}
                          </div>
                        )}
                      </div>
                    </div>

                    {call.notes && (
                      <div className="call-notes-display">
                        <strong>Notes:</strong> {call.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="call-modal-footer">
          <button className="modal-btn secondary" onClick={onClose}>
            Close
          </button>
          <button 
            className="modal-btn primary"
            onClick={() => {
              loadCallHistory();
              alert('Call history refreshed');
            }}
          >
            🔄 Refresh History
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallHistoryModal;